import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plug, Loader2, Copy, CheckCircle2, Unplug } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

export function WordPressDialog({ siteId, siteUrl, connected, onChange }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ site_url: siteUrl || "", username: "", app_password: "" });
  const [busy, setBusy] = useState(false);
  const [plugin, setPlugin] = useState(null);

  useEffect(() => {
    if (open && connected && !plugin) {
      api.get("/system/wp-plugin").then((r) => setPlugin(r.data)).catch(() => {});
    }
  }, [open, connected]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sites/${siteId}/connect-wordpress`, form);
      setPlugin({ code: data.mu_plugin_code, path: data.mu_plugin_path, instructions: data.instructions });
      toast.success("WordPress verified & connected");
      onChange && onChange("wordpress");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete(`/sites/${siteId}/connect-wordpress`);
      setPlugin(null);
      toast.success("WordPress disconnected");
      onChange && onChange("none");
    } catch {
      toast.error("Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  const copyPlugin = () => { navigator.clipboard.writeText(plugin?.code || ""); toast.success("Plugin code copied"); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="wordpress-connect-button">
          <Plug className="mr-2 h-4 w-4" /> {connected ? "WordPress ✓" : "Connect WordPress"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="wordpress-dialog">
        <DialogHeader>
          <DialogTitle>Connect WordPress</DialogTitle>
          <DialogDescription>Push fixes live via the WordPress REST API — no SEO plugin required.</DialogDescription>
        </DialogHeader>

        {!connected && !plugin && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>WordPress site URL</Label>
              <Input data-testid="wp-site-url" className="font-mono" value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })} placeholder="https://client-site.com" />
            </div>
            <div className="space-y-2">
              <Label>Admin username</Label>
              <Input data-testid="wp-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Application password</Label>
              <Input data-testid="wp-app-password" type="password" value={form.app_password} onChange={(e) => setForm({ ...form, app_password: e.target.value })} placeholder="xxxx xxxx xxxx xxxx" />
              <p className="text-xs text-muted-foreground">WP Admin → Users → Profile → Application Passwords. Stored encrypted.</p>
            </div>
            <Button className="w-full" onClick={connect} disabled={busy} data-testid="wp-connect-submit">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plug className="mr-2 h-4 w-4" />} Verify & connect
            </Button>
          </div>
        )}

        {(connected || plugin) && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-4 w-4" /> Connected. Complete the one-time setup below.
            </div>
            <div>
              <p className="mb-1 text-sm font-medium">Step 1 — Create this file on the site:</p>
              <code className="block rounded bg-muted px-2 py-1 font-mono text-xs">{plugin?.path || "wp-content/mu-plugins/beacon-schema.php"}</code>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <p className="text-sm font-medium">Step 2 — Paste this code:</p>
                <Button size="sm" variant="ghost" onClick={copyPlugin} data-testid="wp-copy-plugin"><Copy className="mr-1.5 h-3.5 w-3.5" /> Copy</Button>
              </div>
              <pre className="max-h-56 overflow-auto rounded-md border border-border bg-card p-3 font-mono text-[11px] leading-relaxed">{plugin?.code || "Loading…"}</pre>
              <p className="mt-1 text-xs text-muted-foreground">{plugin?.instructions}</p>
            </div>
            <Button variant="outline" className="w-full" onClick={disconnect} disabled={busy} data-testid="wp-disconnect">
              <Unplug className="mr-2 h-4 w-4" /> Disconnect
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
